import { io } from 'socket.io-client';

let socket = null;
let socketToken = null;

const stripTrailingSlash = (value = '') => String(value || '').replace(/\/+$/, '');

const getDefaultWsUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) {
    return stripTrailingSlash(process.env.REACT_APP_SOCKET_URL);
  }

  if (process.env.REACT_APP_API_URL) {
    try {
      const parsed = new URL(process.env.REACT_APP_API_URL);
      return `${parsed.protocol}//${parsed.host}`;
    } catch {
      // Ignore malformed API URL and fall back to browser location.
    }
  }

  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    const port = window.location.port;
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && (port === '3000' || port === '3001')) {
      return `${window.location.protocol}//${hostname}:5100`;
    }
    return `${window.location.protocol}//${window.location.host}`;
  }
  return 'http://localhost:5000';
};

const resolveWsUrl = () => {
  const envUrl = process.env.REACT_APP_WHOT_WS_URL;
  return envUrl ? stripTrailingSlash(envUrl) : getDefaultWsUrl();
};

export const connectWhotOnlineSocket = (token) => {
  const resolvedUrl = resolveWsUrl();
  const normalizedToken = String(token || '');
  const currentUrl = socket?.io?.uri || null;
  const shouldRecreate = !socket
    || currentUrl !== resolvedUrl
    || socketToken !== normalizedToken;

  if (shouldRecreate) {
    if (socket) {
      try {
        socket.removeAllListeners();
        socket.disconnect();
      } catch {
        // Ignore socket cleanup errors.
      }
    }

    socketToken = normalizedToken;
    // The Whot WS engine is attached to the main HTTP server under /whot-ws/socket.io
    const whotPath = process.env.REACT_APP_WHOT_WS_PATH || '/whot-ws/socket.io';
    socket = io(resolvedUrl, {
      path: whotPath,
      transports: ['websocket', 'polling'],
      auth: { token: normalizedToken },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    return socket;
  }

  if (socket.disconnected) {
    socket.auth = { token: normalizedToken };
    socket.connect();
  }

  return socket;
};

export const getWhotOnlineSocket = () => socket;

export const disconnectWhotOnlineSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  socketToken = null;
};

export const whotOnlineSocket = {
  createRoom: (payload, cb) => {
    if (!socket) {
      if (typeof cb === 'function') cb({ ok: false, error: 'WHOT socket not connected' });
      return;
    }
    socket.emit('whot:create', payload, cb);
  },
  queueJoin: (payload, cb) => {
    if (!socket) return;
    socket.emit('whot:queue:join', payload, cb);
  },
  queueLeave: (payload, cb) => {
    if (!socket) return;
    socket.emit('whot:queue:leave', payload, cb);
  },
  joinRoom: (payload, cb) => {
    if (!socket) {
      if (typeof cb === 'function') cb({ ok: false, error: 'WHOT socket not connected' });
      return;
    }
    socket.emit('whot:join', payload, cb);
  },
  leaveRoom: (payload, cb) => {
    if (!socket) {
      if (typeof cb === 'function') cb({ ok: false, error: 'WHOT socket not connected' });
      return;
    }
    socket.emit('whot:leave', payload, cb);
  },
  startRoom: (payload, cb) => {
    if (!socket) return;
    socket.emit('whot:start', payload, cb);
  },
  spectateRoom: (payload, cb) => {
    if (!socket) return;
    socket.emit('whot:spectate', payload, cb);
  },
  cancelRoom: (payload, cb) => {
    if (!socket) return;
    socket.emit('whot:cancel', payload, cb);
  },
  listRooms: (cb) => {
    if (!socket) return;
    socket.emit('whot:rooms:list', cb);
  },
  sendAction: (payload, cb) => {
    if (!socket) return;
    socket.emit('whot:action', payload, cb);
  },
  requestReplay: (payload, cb) => {
    if (!socket) return;
    socket.emit('whot:replay:request', payload, cb);
  },
  respondReplay: (payload, cb) => {
    if (!socket) return;
    socket.emit('whot:replay:respond', payload, cb);
  },
  sendChat: (payload, cb) => {
    if (!socket) return;
    socket.emit('whot:chat:send', payload, cb);
  },
  onState: (cb) => {
    if (!socket) return;
    socket.on('whot:state', cb);
  },
  onError: (cb) => {
    if (!socket) return;
    socket.on('whot:error', cb);
  },
  onQueueMatched: (cb) => {
    if (!socket) return;
    socket.on('whot:queue:matched', cb);
  },
  onQueueError: (cb) => {
    if (!socket) return;
    socket.on('whot:queue:error', cb);
  },
  onRoomsUpdate: (cb) => {
    if (!socket) return;
    socket.on('whot:rooms:update', cb);
  },
  onReplayRequest: (cb) => {
    if (!socket) return;
    socket.on('whot:replay:request', cb);
  },
  onReplayUpdate: (cb) => {
    if (!socket) return;
    socket.on('whot:replay:update', cb);
  },
  onReplayCancel: (cb) => {
    if (!socket) return;
    socket.on('whot:replay:cancel', cb);
  },
  onReplayReady: (cb) => {
    if (!socket) return;
    socket.on('whot:replay:ready', cb);
  },
  onChatHistory: (cb) => {
    if (!socket) return;
    socket.on('whot:chat:history', cb);
  },
  onChatMessage: (cb) => {
    if (!socket) return;
    socket.on('whot:chat:new', cb);
  },
  onEliminated: (cb) => {
    if (!socket) return;
    socket.on('whot:eliminated', cb);
  },
  removeListeners: () => {
    if (!socket) return;
    socket.off('whot:state');
    socket.off('whot:error');
    socket.off('whot:queue:matched');
    socket.off('whot:queue:error');
    socket.off('whot:rooms:update');
    socket.off('whot:replay:request');
    socket.off('whot:replay:update');
    socket.off('whot:replay:cancel');
    socket.off('whot:replay:ready');
    socket.off('whot:chat:history');
    socket.off('whot:chat:new');
    socket.off('whot:eliminated');
  },
};
